import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Shield, ShieldOff, Ban, CheckCircle, Trash2, Key, RefreshCw, Eye } from 'lucide-react';
import UserDetailDrawer from './UserDetailDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [detailUserId, setDetailUserId] = useState(null);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, ...(search && { search }), ...(statusFilter !== 'all' && { status: statusFilter }) };
      const res = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateUser = async (userId, update) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, update, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Error updating user'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Error deleting user'); }
  };

  const revokeAllSessions = async (userId) => {
    try {
      await axios.delete(`${API}/admin/sessions/user/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert('Error revoking sessions'); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250, position: 'relative' }}>
          <Search size={16} color="#8885a0" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by name or email..."
            style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'Inter, sans-serif' }}
          />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#b0adc0', fontSize: 13, outline: 'none', fontFamily: 'Inter, sans-serif' }}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
        </select>
        <button onClick={fetchUsers} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#b0adc0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <RefreshCw size={14} /> Refresh
        </button>
        <span style={{ fontSize: 13, color: '#8885a0' }}>{total} users total</span>
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(88,28,135,0.08)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(139,92,246,0.15)' }}>
              {['User', 'Email', 'Status', 'Role', 'Sessions', 'Last Login', 'Joined', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#8885a0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#8885a0' }}>Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#8885a0' }}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.user_id} style={{ borderBottom: '1px solid rgba(139,92,246,0.08)' }}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {u.picture ? <img src={u.picture} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} /> : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(139,92,246,0.2)' }} />}
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#e0dff0' }}>{u.name || 'Unnamed'}</span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#b0adc0' }}>{u.email}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: u.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: u.status === 'active' ? '#22c55e' : '#ef4444' }}>
                    {u.status || 'active'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: u.is_admin ? 'rgba(139,92,246,0.2)' : 'rgba(136,133,160,0.1)', color: u.is_admin ? '#c4b5fd' : '#8885a0' }}>
                    {u.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#b0adc0', textAlign: 'center' }}>{u.active_sessions || 0}</td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: '#8885a0' }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}</td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: '#8885a0' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button data-testid={`view-user-${u.user_id}`} onClick={() => setDetailUserId(u.user_id)} title="View Details" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(0,212,255,0.12)', color: '#00d4ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Eye size={14} />
                    </button>
                    <button onClick={() => updateUser(u.user_id, { is_admin: !u.is_admin })} title={u.is_admin ? 'Remove Admin' : 'Make Admin'} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(139,92,246,0.12)', color: u.is_admin ? '#c4b5fd' : '#8885a0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {u.is_admin ? <ShieldOff size={14} /> : <Shield size={14} />}
                    </button>
                    <button onClick={() => updateUser(u.user_id, { status: u.status === 'banned' ? 'active' : 'banned' })} title={u.status === 'banned' ? 'Unban' : 'Ban'} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: u.status === 'banned' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: u.status === 'banned' ? '#22c55e' : '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {u.status === 'banned' ? <CheckCircle size={14} /> : <Ban size={14} />}
                    </button>
                    <button onClick={() => revokeAllSessions(u.user_id)} title="Revoke Sessions" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Key size={14} />
                    </button>
                    <button onClick={() => deleteUser(u.user_id)} title="Delete" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.12)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#b0adc0', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1, fontSize: 13 }}>Previous</button>
          <span style={{ padding: '8px 16px', fontSize: 13, color: '#8885a0' }}>Page {page + 1} of {Math.ceil(total / limit)}</span>
          <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(88,28,135,0.12)', color: '#b0adc0', cursor: (page + 1) * limit >= total ? 'default' : 'pointer', opacity: (page + 1) * limit >= total ? 0.4 : 1, fontSize: 13 }}>Next</button>
        </div>
      )}
      {/* Detail Drawer */}
      {detailUserId && (
        <UserDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} />
      )}
    </div>
  );
};

export default AdminUsers;
